import { Ship } from './formTypes';
import { useLocalization } from '@ais-ui/locale-management';
import { useMutateShiplistContext } from './MutateShiplistContext';
import React, { useMemo, useRef, useState } from 'react';
import { ShipSearchResult } from '../../../Map/Search/useShipSearch';
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { noop } from 'lodash';
import { cn } from '@ais-ui/utils';
import { PiMagnifyingGlass, PiMinusThin, PiWarning } from 'react-icons/pi';
import { ShipSearchInput } from '../../../Map/Search/ShipPortSearch';
import { AISShipSearch } from '../../../Map/Search/AISShipSearch';

interface SearchPopupProps {
  onClickShip: (ship: ShipSearchResult, index: number) => void;
  shiplistIndex: number;
}

const SearchPopup = ({ onClickShip, shiplistIndex }: SearchPopupProps) => {
  const [searchString, setSearchString] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(true);

      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);

      return;
    }

    if (searchString.length === 0) {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <ShipSearchInput
        className="border rounded-md"
        searchString={searchString}
        setSearchString={setSearchString}
        setOpen={setOpen}
        inputRef={inputRef}
      ></ShipSearchInput>
      <PopoverContent className="w-80">
        <AISShipSearch
          searchString={searchString}
          onClick={(ship) => {
            onClickShip(ship, shiplistIndex);
          }}
        ></AISShipSearch>
      </PopoverContent>
    </Popover>
  );
};

interface ShiplistShipRowProps {
  ship: Ship;
  index: number;
}

export const ShiplistShipRow = ({ ship, index }: ShiplistShipRowProps) => {
  const { messages } = useLocalization();

  const { invalidIndices, form, fields, append, remove, update } =
    useMutateShiplistContext();

  const hasMmsiWarning = useMemo(() => {
    return invalidIndices.mmsi.includes(index);
  }, [invalidIndices.mmsi, index]);

  const hasImoWarning = useMemo(() => {
    return invalidIndices.imo.includes(index);
  }, [invalidIndices.imo, index]);

  const hasWarning = useMemo(() => {
    return hasMmsiWarning || hasImoWarning;
  }, [hasMmsiWarning, hasImoWarning]);

  const hasError = useMemo(() => {
    return Boolean(
      form.formState.errors?.ships?.[index]?.root ||
        form.formState.errors?.ships?.[index]?.mmsi
    );
  }, [form.formState.errors?.ships, index]);

  const onClickShip = (ship: ShipSearchResult, index: number) => {
    let mmsi, imo;

    if (typeof ship.mmsi === 'string') {
      mmsi = parseInt(ship.mmsi) || undefined;
    } else if (typeof ship.mmsi === 'number') {
      mmsi = ship.mmsi;
    } else {
      mmsi = undefined;
    }

    if (typeof ship.imo === 'string') {
      imo = parseInt(ship.imo) || undefined;
    } else if (typeof ship.imo === 'number') {
      imo = ship.imo;
    } else {
      imo = undefined;
    }

    const formShip: Ship = {
      id: fields[index].id,
      name: ship.name ? ship.name : '',
      mmsi: mmsi,
      imo: imo,
    };

    form.setValue(`ships.${index}`, formShip);
    update(index, formShip);
  };

  const tooltipWarning = useMemo(() => {
    if (hasMmsiWarning) {
      return messages.shiplistShipRowHasMmsiWarningReturn;
    } else if (hasImoWarning) {
      const imoString = ship?.imo?.toString() || '';
      if (imoString.length !== 7) {
        return messages.shiplistShipRowHasImoWarningReturn;
      } else {
        return messages.shiplistShipRowInvalidChecksumImoNumberReturn;
      }
    }
  }, [hasMmsiWarning, hasImoWarning, ship?.imo]);

  return (
    <div className="flex space-x-2 items-center w-full">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={noop}
            className={cn(
              'h-9 w-9 px-2 flex items-center',
              'disabled:opacity-100',
              'hover:bg-transparent hover:opacity-100 cursor-default',
              hasError &&
                'font-medium text-destructive hover:text-destructive cursor-pointer w-12',
              hasWarning &&
                'font-medium text-yellow-500 hover:text-yellow-500 cursor-pointer w-12'
            )}
          >
            {(hasError || hasWarning) && (
              <PiWarning className="h-4 w-4 shrink-0 ml-2 mr-1"></PiWarning>
            )}
            <span className="inline-block">{index + 1}</span>
          </Button>
        </TooltipTrigger>
        {hasWarning && <TooltipContent>{tooltipWarning}</TooltipContent>}
      </Tooltip>
      <div className="flex-col space-y-2 w-full">
        <FormField
          control={form.control}
          name={`ships.${index}.name`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder={messages.shiplistsSaveShipNameLabel}
                  className="flex-grow"
                  style={{ flexBasis: '50%' }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex-col space-y-2">
        <FormField
          control={form.control}
          name={`ships.${index}.mmsi`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder={messages.shiplistsSaveMMSILabel}
                  className={cn(
                    'w-32',
                    hasMmsiWarning &&
                      'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500'
                  )}
                  type={'number'}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      field.onChange(null);
                    } else {
                      const numValue = parseInt(value, 10);
                      field.onChange(numValue);
                    }
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex-col space-y-2">
        <FormField
          control={form.control}
          name={`ships.${index}.imo`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder={messages.shiplistsSaveIMOLabel}
                  className={cn(
                    'w-28',
                    hasImoWarning &&
                      'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500'
                  )}
                  type={'number'}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      field.onChange(null);
                    } else {
                      const numValue = parseInt(value, 10);
                      field.onChange(numValue);
                    }
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex min-w-20 space-x-2">
        <Popover modal={true}>
          <PopoverTrigger asChild>
            <Button
              id="search-ship-button"
              type="button"
              size="icon"
              variant="outline"
              className="h-9 w-9"
            >
              <PiMagnifyingGlass className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[355px]">
            <SearchPopup onClickShip={onClickShip} shiplistIndex={index} />
          </PopoverContent>
        </Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              id="remove-ship-button"
              type="button"
              size="icon"
              variant="outline"
              className="h-9 w-9"
              onClick={() => remove(index)}
            >
              <PiMinusThin className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {messages.shiplistsSaveRemoveShipTooltip}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
